import React, { useRef, useState, useEffect } from 'react';
import { Check, Edit, Eye, EyeOff } from 'lucide-react';
import { cn, formatCurrency, formatDate } from '../../../lib/utils';
import { contratosApi } from '../../../lib/api';
import { facturacionApi } from '../../../lib/api';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../ui/tabs';
import { formatMoneyInput, parseMoneyInput } from './helpers';
import { EmailsFacturacionList } from './EmailsFacturacionList';
import { AccesoInquilinoSection } from './AccesoInquilinoSection';
import { VerazSection } from './VerazSection';
import { DocumentosSection } from './DocumentosSection';
import { CocherasModoSection } from './CocherasModoSection';

// Historial de ajustes manuales (monto manual cargado por período en 5. Factura).
// Se muestra en la solapa Condiciones para dejar trazabilidad de en qué mes se hizo.
const AjustesManualesSection = ({ contratoId }) => {
  const [ajustes, setAjustes] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!contratoId) return;
    let activo = true;
    setLoading(true);
    facturacionApi.getAjustesManuales(contratoId)
      .then(res => { if (activo) setAjustes(res.data?.ajustes || []); })
      .catch(() => { if (activo) setAjustes([]); })
      .finally(() => { if (activo) setLoading(false); });
    return () => { activo = false; };
  }, [contratoId]);

  if (loading || ajustes.length === 0) return null;

  return (
    <div
      className="rounded-lg border border-amber-200 bg-amber-50 p-3"
      data-testid="ajustes-manuales-section"
    >
      <h4 className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-2">
        <Edit className="w-4 h-4" />
        Ajustes manuales aplicados
      </h4>
      <p className="text-xs text-amber-700 mb-2">
        Se cargó un monto manual en los siguientes períodos. A partir de cada ajuste manual,
        los cálculos posteriores toman ese valor como base.
      </p>
      <ul className="space-y-1" data-testid="ajustes-manuales-list">
        {ajustes.map((a, i) => (
          <li
            key={`${a.unidad_id}-${a.periodo}-${i}`}
            className="text-xs text-amber-900 flex items-center justify-between gap-2 border-t border-amber-200 pt-1 first:border-t-0 first:pt-0"
            data-testid={`ajuste-manual-item-${a.periodo}`}
          >
            <span className="font-medium">{a.periodo_label || a.periodo}</span>
            <span>{formatCurrency(a.monto_manual)}</span>
            {a.unidad_nombre ? <span className="text-amber-600">· {a.unidad_nombre}</span> : null}
          </li>
        ))}
      </ul>
    </div>
  );
};

// Edición de unidad principal + cocheras del contrato (Fase 4)
const UnidadesEditSection = ({ unidades, contratos, editContratoForm, setEditContratoForm, isEditMode, setIsEditMode }) => {
  const ucList = editContratoForm.unidades_contrato || [];
  const unidadById = Object.fromEntries(unidades.map(u => [u.unidad_id, u]));
  // Los items de unidades_contrato en Mongo no traen `tipo`; resolverlo desde el catálogo de unidades.
  const esCochera = (uc) => (unidadById[uc.unidad_id]?.tipo === 'cochera') || (uc.tipo === 'cochera') || (uc.tipo_cochera != null);
  const principalActual = ucList.find(u => !esCochera(u))?.unidad_id || editContratoForm.unidad_id || '';
  const cocherasActuales = ucList.filter(u => esCochera(u)).map(u => u.unidad_id);

  const ocupadasOtros = new Set();
  (contratos || []).forEach(c => {
    if (c.contrato_id === editContratoForm.contrato_id) return;
    if (c.estado !== 'vigente') return;
    (c.unidades_contrato || []).forEach(u => ocupadasOtros.add(u.unidad_id));
    if (c.unidad_id) ocupadasOtros.add(c.unidad_id);
  });
  const propias = new Set(ucList.map(u => u.unidad_id));
  const disponible = (uid) => !ocupadasOtros.has(uid) || propias.has(uid);

  const cocheras = unidades.filter(u => u && u.tipo === 'cochera');

  const toggleCochera = (uid) => {
    const exists = cocherasActuales.includes(uid);
    const nuevo = exists
      ? ucList.filter(x => x.unidad_id !== uid)
      : [...ucList, { unidad_id: uid, tipo: 'cochera', monto: 0, tipo_cochera: 'estandar' }];
    setEditContratoForm({ ...editContratoForm, unidades_contrato: nuevo });
  };

  return (
    <div className="space-y-4" data-testid="unidades-edit-section">
      <div>
        <Label className="text-sm font-medium">Unidad principal</Label>
        <div
          className="mt-1 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-700 flex items-center justify-between"
          data-testid="principal-readonly-edit"
        >
          <span className="font-medium">{unidadById[principalActual]?.nombre_unidad || principalActual || '—'}</span>
          <span className="text-xs text-slate-400">Solo lectura</span>
        </div>
        <p className="text-xs text-slate-400 mt-1">La unidad principal no se modifica desde la edición. Para cambiarla, finalizá el contrato y creá uno nuevo.</p>
      </div>

      <div>
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">Cocheras asociadas</Label>
          {!isEditMode && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 text-[11px] px-2"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsEditMode && setIsEditMode(true); }}
              data-testid="editar-cocheras-btn"
            >
              <Edit className="w-3 h-3 mr-1" /> Editar cocheras
            </Button>
          )}
        </div>
        {isEditMode && (
          <p className="text-xs text-violet-600 mt-1" data-testid="cocheras-edit-hint">
            Modo edición activo: marcá/desmarcá cocheras y guardá con "Guardar Cambios".
          </p>
        )}
        <div className="mt-1 grid grid-cols-3 gap-2 max-h-56 overflow-y-auto border border-slate-200 rounded-lg p-2" data-testid="cocheras-edit-list">
          {cocheras.length === 0 && <p className="text-xs text-slate-400 col-span-3 p-2">No hay cocheras en este edificio.</p>}
          {cocheras.map(u => {
            const checked = cocherasActuales.includes(u.unidad_id);
            const ocupadaOtro = !disponible(u.unidad_id);
            return (
              <label
                key={u.unidad_id}
                className={cn(
                  "flex items-center gap-2 text-sm px-2 py-1.5 rounded border",
                  checked ? "border-violet-300 bg-violet-50" : "border-slate-200",
                  (ocupadaOtro || !isEditMode) ? "opacity-60" : "cursor-pointer hover:bg-slate-50"
                )}
                title={ocupadaOtro ? 'Ocupada por otro contrato vigente' : ''}
              >
                <input
                  type="checkbox"
                  className="rounded border-slate-300"
                  checked={checked}
                  disabled={!isEditMode || (ocupadaOtro && !checked)}
                  onChange={() => toggleCochera(u.unidad_id)}
                  data-testid={`cochera-check-${u.unidad_id}`}
                />
                <span>{u.nombre_unidad}{ocupadaOtro && !checked ? ' (ocupada)' : ''}</span>
              </label>
            );
          })}
        </div>
        <p className="text-xs text-slate-400 mt-1">
          {cocherasActuales.length} cochera(s) seleccionada(s). La valuación se ajusta en la solapa Condiciones.
        </p>
      </div>
    </div>
  );
};


export const EditContratoDialog = ({
  open,
  onOpenChange,
  editContratoForm,
  setEditContratoForm,
  editContratoTab,
  setEditContratoTab,
  isEditMode,
  setIsEditMode,
  saving,
  handleSaveEditContrato,
  unidades,
  contratos,
  onFinalizarContrato,
  userRol,
}) => {
  const [montoActHoy, setMontoActHoy] = useState(null);
  const [showProvPass, setShowProvPass] = useState(false);

  useEffect(() => {
    if (!open) return;
    const mi = parseFloat(editContratoForm?.monto_inicial);
    const fi = editContratoForm?.fecha_inicio;
    if (!mi || mi <= 0 || !fi) { setMontoActHoy(null); return; }
    const indices = editContratoForm?.ajuste_indices || [];
    const esPorcentaje = (editContratoForm?.indice_tipo === 'porcentaje');
    const sinAjuste = (editContratoForm?.indice_tipo === 'sin_ajuste');
    const payload = {
      monto_inicial: mi,
      fecha_inicio: fi,
      indice_tipo: sinAjuste ? 'sin_ajuste' : (esPorcentaje ? 'porcentaje' : 'ipc'),
      indice_frecuencia: editContratoForm?.ajuste_frecuencia || editContratoForm?.indice_frecuencia || 'trimestral',
      indice_valor_porcentaje: esPorcentaje ? (parseFloat(editContratoForm?.indice_valor) || null) : null,
      ajuste_indices: sinAjuste ? [{ tipo: 'sin_ajuste', peso: 100 }] : ((indices && indices.length > 0) ? indices : null),
    };
    let cancel = false;
    contratosApi.calcularMontoActualizado(payload)
      .then((r) => { if (!cancel) setMontoActHoy(r.data); })
      .catch(() => { if (!cancel) setMontoActHoy(null); });
    return () => { cancel = true; };
  }, [open, editContratoForm?.monto_inicial, editContratoForm?.fecha_inicio,
      editContratoForm?.indice_tipo, editContratoForm?.indice_frecuencia,
      editContratoForm?.ajuste_frecuencia, editContratoForm?.indice_valor]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="w-5 h-5 text-blue-600" />
            {isEditMode ? 'Editar Contrato' : 'Ver Contrato'}
          </DialogTitle>
        </DialogHeader>
        {editContratoForm && (
          <form onSubmit={handleSaveEditContrato}>
            <Tabs value={editContratoTab} onValueChange={setEditContratoTab} className="w-full">
              <TabsList className="grid w-full grid-cols-8 mb-4">
                <TabsTrigger value="documentos" data-testid="tab-documentos">Documentos</TabsTrigger>
                <TabsTrigger value="unidades" data-testid="tab-unidades-edit">Unidades</TabsTrigger>
                <TabsTrigger value="basico">Datos</TabsTrigger>
                <TabsTrigger value="condiciones">Condiciones</TabsTrigger>
                <TabsTrigger value="fiscal">Fiscal</TabsTrigger>
                <TabsTrigger value="veraz" data-testid="tab-veraz">Veraz</TabsTrigger>
                <TabsTrigger value="colppy">Colppy</TabsTrigger>
                <TabsTrigger value="inquilino" data-testid="tab-inquilino">Inquilino</TabsTrigger>
              </TabsList>

              {/* Tab: Documentos (etapas contractuales) */}
              <TabsContent value="documentos" className="space-y-4">
                <DocumentosSection contratoId={editContratoForm.contrato_id} readOnly={!isEditMode} />
              </TabsContent>

              {/* Tab: Unidades (Fase 4: edición de unidad principal + cocheras) */}
              <TabsContent value="unidades" className="space-y-4">
                <UnidadesEditSection
                  unidades={unidades || []}
                  contratos={contratos || []}
                  editContratoForm={editContratoForm}
                  setEditContratoForm={setEditContratoForm}
                  isEditMode={isEditMode}
                  setIsEditMode={setIsEditMode}
                />
              </TabsContent>

              {/* Tab: Datos */}
              <TabsContent value="basico" className="space-y-4">
                {/* Tipo de inquilino — primer campo a definir */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <Label className="text-sm font-medium mb-2 block">Tipo de inquilino *</Label>
                  <div className="flex gap-2">
                    {[
                      { value: 'humana', label: 'Persona humana' },
                      { value: 'juridica', label: 'Persona jurídica' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        disabled={!isEditMode}
                        onClick={() => setEditContratoForm({ ...editContratoForm, tipo_inquilino: opt.value })}
                        data-testid={`tipo-inquilino-${opt.value}`}
                        className={`px-4 py-1.5 rounded-md text-sm font-medium border transition-colors ${
                          (editContratoForm.tipo_inquilino || 'juridica') === opt.value
                            ? 'bg-emerald-600 text-white border-emerald-600'
                            : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                        } ${!isEditMode ? 'opacity-60 cursor-not-allowed' : ''}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {(editContratoForm.tipo_inquilino || 'juridica') === 'humana' ? (
                  <>
                    {/* === PERSONA HUMANA === */}
                    <div>
                      <h4 className="text-sm font-medium text-slate-700 mb-3">Datos del firmante</h4>
                      <div className="grid grid-cols-2 gap-4 mb-3">
                        <div className="space-y-2">
                          <Label>Nombre y apellido *</Label>
                          <Input
                            value={editContratoForm.firmante_nombre || ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              setEditContratoForm(f => ({
                                ...f,
                                firmante_nombre: v,
                                ...(f.contacto_comercial_mismo_firmante !== false ? { contacto_comercial_nombre: v } : {}),
                                ...(f.contacto_admin_mismo_firmante !== false ? { contacto_admin_nombre: v } : {}),
                                // razon_social = nombre del firmante (persona humana es el "inquilino contractual")
                                razon_social: v,
                              }));
                            }}
                            placeholder="Nombre y apellido"
                            disabled={!isEditMode}
                            className={!isEditMode ? 'bg-slate-50' : ''}
                            data-testid="firmante-nombre"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Email *</Label>
                          <Input
                            type="email"
                            value={editContratoForm.firmante_email || ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              setEditContratoForm(f => ({
                                ...f,
                                firmante_email: v,
                                ...(f.contacto_comercial_mismo_firmante !== false ? { contacto_comercial_email: v } : {}),
                                ...(f.contacto_admin_mismo_firmante !== false ? { contacto_admin_email: v } : {}),
                                ...((!f.email_facturacion || f.email_facturacion_default) ? { email_facturacion: v, email_facturacion_default: true } : {}),
                              }));
                            }}
                            placeholder="email@ejemplo.com"
                            disabled={!isEditMode}
                            className={!isEditMode ? 'bg-slate-50' : ''}
                            data-testid="firmante-email"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>WhatsApp</Label>
                          <Input
                            type="tel"
                            value={editContratoForm.firmante_whatsapp || ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              setEditContratoForm(f => ({
                                ...f,
                                firmante_whatsapp: v,
                                ...(f.contacto_comercial_mismo_firmante !== false ? { contacto_comercial_telefono: v } : {}),
                                ...(f.contacto_admin_mismo_firmante !== false ? { contacto_admin_telefono: v } : {}),
                              }));
                            }}
                            placeholder="+54 261 123-4567"
                            disabled={!isEditMode}
                            className={!isEditMode ? 'bg-slate-50' : ''}
                            data-testid="firmante-whatsapp"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Descripción de la actividad</Label>
                          <Input
                            value={editContratoForm.descripcion_empresa || ''}
                            onChange={(e) => setEditContratoForm({...editContratoForm, descripcion_empresa: e.target.value})}
                            placeholder="Ej: Profesional independiente, freelance, etc."
                            disabled={!isEditMode}
                            className={!isEditMode ? 'bg-slate-50' : ''}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Contacto Comercial (Humana) */}
                    <div className="border-t pt-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-slate-700">Contacto Comercial</h4>
                        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editContratoForm.contacto_comercial_mismo_firmante !== false}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setEditContratoForm(f => ({
                                ...f,
                                contacto_comercial_mismo_firmante: checked,
                                ...(checked ? {
                                  contacto_comercial_nombre: f.firmante_nombre,
                                  contacto_comercial_email: f.firmante_email,
                                  contacto_comercial_telefono: f.firmante_whatsapp,
                                } : {}),
                              }));
                            }}
                            disabled={!isEditMode}
                            data-testid="comercial-mismo-firmante"
                          />
                          Mismo que el firmante
                        </label>
                      </div>
                      {editContratoForm.contacto_comercial_mismo_firmante === false && (
                        <div className="grid grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label>Nombre y apellido</Label>
                            <Input value={editContratoForm.contacto_comercial_nombre || ''} onChange={(e) => setEditContratoForm({...editContratoForm, contacto_comercial_nombre: e.target.value})} placeholder="Nombre y apellido" disabled={!isEditMode} className={!isEditMode ? 'bg-slate-50' : ''} />
                          </div>
                          <div className="space-y-2">
                            <Label>Email</Label>
                            <Input type="email" value={editContratoForm.contacto_comercial_email || ''} onChange={(e) => setEditContratoForm({...editContratoForm, contacto_comercial_email: e.target.value})} placeholder="email@ejemplo.com" disabled={!isEditMode} className={!isEditMode ? 'bg-slate-50' : ''} />
                          </div>
                          <div className="space-y-2">
                            <Label>WhatsApp</Label>
                            <Input type="tel" value={editContratoForm.contacto_comercial_telefono || ''} onChange={(e) => setEditContratoForm({...editContratoForm, contacto_comercial_telefono: e.target.value})} placeholder="+54 261 123-4567" disabled={!isEditMode} className={!isEditMode ? 'bg-slate-50' : ''} />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Contacto Administrativo (Humana) */}
                    <div className="border-t pt-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-slate-700">Contacto Administrativo</h4>
                        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editContratoForm.contacto_admin_mismo_firmante !== false}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setEditContratoForm(f => ({
                                ...f,
                                contacto_admin_mismo_firmante: checked,
                                ...(checked ? {
                                  contacto_admin_nombre: f.firmante_nombre,
                                  contacto_admin_email: f.firmante_email,
                                  contacto_admin_telefono: f.firmante_whatsapp,
                                } : {}),
                              }));
                            }}
                            disabled={!isEditMode}
                            data-testid="admin-mismo-firmante"
                          />
                          Mismo que el firmante
                        </label>
                      </div>
                      {editContratoForm.contacto_admin_mismo_firmante === false && (
                        <div className="grid grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label>Nombre y apellido</Label>
                            <Input value={editContratoForm.contacto_admin_nombre || ''} onChange={(e) => setEditContratoForm({...editContratoForm, contacto_admin_nombre: e.target.value})} placeholder="Nombre y apellido" disabled={!isEditMode} className={!isEditMode ? 'bg-slate-50' : ''} />
                          </div>
                          <div className="space-y-2">
                            <Label>Email</Label>
                            <Input type="email" value={editContratoForm.contacto_admin_email || ''} onChange={(e) => setEditContratoForm({...editContratoForm, contacto_admin_email: e.target.value})} placeholder="email@ejemplo.com" disabled={!isEditMode} className={!isEditMode ? 'bg-slate-50' : ''} />
                          </div>
                          <div className="space-y-2">
                            <Label>WhatsApp</Label>
                            <Input type="tel" value={editContratoForm.contacto_admin_telefono || ''} onChange={(e) => setEditContratoForm({...editContratoForm, contacto_admin_telefono: e.target.value})} placeholder="+54 261 123-4567" disabled={!isEditMode} className={!isEditMode ? 'bg-slate-50' : ''} />
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    {/* === PERSONA JURÍDICA === */}
                    <div>
                      <h4 className="text-sm font-medium text-slate-700 mb-3">Datos de la empresa</h4>
                      <div className="grid grid-cols-2 gap-4 mb-3">
                        <div className="space-y-2">
                          <Label>Razón Social *</Label>
                          <Input value={editContratoForm.razon_social} onChange={(e) => setEditContratoForm({...editContratoForm, razon_social: e.target.value})} placeholder="Razón social" required disabled={!isEditMode} className={!isEditMode ? 'bg-slate-50' : ''} data-testid="razon-social-input" />
                        </div>
                        <div className="space-y-2">
                          <Label>Nombre Comercial</Label>
                          <Input value={editContratoForm.nombre_comercial || ''} onChange={(e) => setEditContratoForm({...editContratoForm, nombre_comercial: e.target.value})} placeholder="Nombre comercial" disabled={!isEditMode} className={!isEditMode ? 'bg-slate-50' : ''} />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Descripción de la empresa</Label>
                        <Input value={editContratoForm.descripcion_empresa || ''} onChange={(e) => setEditContratoForm({...editContratoForm, descripcion_empresa: e.target.value})} placeholder="Ej: Consultora de tecnología, Estudio contable, etc." disabled={!isEditMode} className={!isEditMode ? 'bg-slate-50' : ''} />
                      </div>
                    </div>

                    {/* Contacto Comercial (Jurídica) */}
                    <div className="border-t pt-4">
                      <h4 className="text-sm font-medium text-slate-700 mb-3">Contacto Comercial</h4>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>Nombre y apellido</Label>
                          <Input
                            value={editContratoForm.contacto_comercial_nombre || ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              setEditContratoForm(f => ({
                                ...f,
                                contacto_comercial_nombre: v,
                                ...(f.contacto_admin_mismo_comercial !== false ? { contacto_admin_nombre: v } : {}),
                              }));
                            }}
                            placeholder="Nombre y apellido"
                            disabled={!isEditMode}
                            className={!isEditMode ? 'bg-slate-50' : ''}
                            data-testid="comercial-nombre"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Email</Label>
                          <Input
                            type="email"
                            value={editContratoForm.contacto_comercial_email || ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              setEditContratoForm(f => ({
                                ...f,
                                contacto_comercial_email: v,
                                ...(f.contacto_admin_mismo_comercial !== false ? { contacto_admin_email: v } : {}),
                                ...((!f.email_facturacion || f.email_facturacion_default) ? { email_facturacion: v, email_facturacion_default: true } : {}),
                              }));
                            }}
                            placeholder="email@ejemplo.com"
                            disabled={!isEditMode}
                            className={!isEditMode ? 'bg-slate-50' : ''}
                            data-testid="comercial-email"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>WhatsApp / Teléfono</Label>
                          <Input
                            type="tel"
                            value={editContratoForm.contacto_comercial_telefono || ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              setEditContratoForm(f => ({
                                ...f,
                                contacto_comercial_telefono: v,
                                ...(f.contacto_admin_mismo_comercial !== false ? { contacto_admin_telefono: v } : {}),
                              }));
                            }}
                            placeholder="+54 261 123-4567"
                            disabled={!isEditMode}
                            className={!isEditMode ? 'bg-slate-50' : ''}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Contacto Administrativo (Jurídica) */}
                    <div className="border-t pt-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-slate-700">Contacto Administrativo</h4>
                        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editContratoForm.contacto_admin_mismo_comercial !== false}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setEditContratoForm(f => ({
                                ...f,
                                contacto_admin_mismo_comercial: checked,
                                ...(checked ? {
                                  contacto_admin_nombre: f.contacto_comercial_nombre,
                                  contacto_admin_email: f.contacto_comercial_email,
                                  contacto_admin_telefono: f.contacto_comercial_telefono,
                                } : {}),
                              }));
                            }}
                            disabled={!isEditMode}
                            data-testid="admin-mismo-comercial"
                          />
                          Mismo que el contacto comercial
                        </label>
                      </div>
                      {editContratoForm.contacto_admin_mismo_comercial === false && (
                        <div className="grid grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label>Nombre y apellido</Label>
                            <Input value={editContratoForm.contacto_admin_nombre || ''} onChange={(e) => setEditContratoForm({...editContratoForm, contacto_admin_nombre: e.target.value})} placeholder="Nombre y apellido" disabled={!isEditMode} className={!isEditMode ? 'bg-slate-50' : ''} />
                          </div>
                          <div className="space-y-2">
                            <Label>Email</Label>
                            <Input type="email" value={editContratoForm.contacto_admin_email || ''} onChange={(e) => setEditContratoForm({...editContratoForm, contacto_admin_email: e.target.value})} placeholder="email@ejemplo.com" disabled={!isEditMode} className={!isEditMode ? 'bg-slate-50' : ''} />
                          </div>
                          <div className="space-y-2">
                            <Label>WhatsApp / Teléfono</Label>
                            <Input type="tel" value={editContratoForm.contacto_admin_telefono || ''} onChange={(e) => setEditContratoForm({...editContratoForm, contacto_admin_telefono: e.target.value})} placeholder="+54 261 123-4567" disabled={!isEditMode} className={!isEditMode ? 'bg-slate-50' : ''} />
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Contacto de emergencia — Fase 3 */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-3" data-testid="edit-contacto-emergencia">
                  <Label className="text-sm font-medium">Contacto de emergencia</Label>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Nombre</Label>
                      <Input value={editContratoForm.emergencia_nombre || ''} onChange={(e) => setEditContratoForm({...editContratoForm, emergencia_nombre: e.target.value})} placeholder="Nombre y apellido" disabled={!isEditMode} className={!isEditMode ? 'bg-slate-50' : ''} data-testid="edit-emergencia-nombre" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Teléfono</Label>
                      <Input type="tel" value={editContratoForm.emergencia_telefono || ''} onChange={(e) => setEditContratoForm({...editContratoForm, emergencia_telefono: e.target.value})} placeholder="+54 261 123-4567" disabled={!isEditMode} className={!isEditMode ? 'bg-slate-50' : ''} data-testid="edit-emergencia-telefono" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Email</Label>
                      <Input type="email" value={editContratoForm.emergencia_email || ''} onChange={(e) => setEditContratoForm({...editContratoForm, emergencia_email: e.target.value})} placeholder="email@ejemplo.com" disabled={!isEditMode} className={!isEditMode ? 'bg-slate-50' : ''} data-testid="edit-emergencia-email" />
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Tab: Condiciones */}
              <TabsContent value="condiciones" className="space-y-4">
                <AjustesManualesSection contratoId={editContratoForm.contrato_id} />
                {/* Vigencia */}
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-3">Vigencia</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Fecha Inicio *</Label>
                      <Input
                        type="date"
                        value={editContratoForm.fecha_inicio}
                        onChange={(e) => {
                          const newFechaInicio = e.target.value;
                          setEditContratoForm(prev => ({
                            ...prev,
                            fecha_inicio: newFechaInicio,
                            fecha_fin: prev.fecha_fin && prev.fecha_fin < newFechaInicio ? '' : prev.fecha_fin
                          }));
                        }}
                        required
                        disabled={!isEditMode}
                        className={!isEditMode ? 'bg-slate-50' : ''}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Fecha Fin *</Label>
                      <Input
                        type="date"
                        value={editContratoForm.fecha_fin}
                        onChange={(e) => setEditContratoForm({...editContratoForm, fecha_fin: e.target.value})}
                        min={editContratoForm.fecha_inicio || undefined}
                        required
                        disabled={!isEditMode}
                        className={!isEditMode ? 'bg-slate-50' : ''}
                      />
                    </div>
                  </div>
                </div>

                {/* Montos */}
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-slate-700 mb-1">Montos</h4>
                  <p className="text-xs text-slate-400 mb-3">Todos los valores son netos (sin IVA)</p>
                  <div className="grid grid-cols-4 gap-3 mb-4">
                    <div className="space-y-2">
                      <Label className="text-xs">Monto Inicio Contrato *</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">{editContratoForm.moneda === 'USD' ? 'US$' : '$'}</span>
                        <Input type="text" value={formatMoneyInput(editContratoForm.monto_inicial)} onChange={(e) => setEditContratoForm({...editContratoForm, monto_inicial: parseMoneyInput(e.target.value)})} placeholder="0" className={cn("pl-10 text-right", !isEditMode && 'bg-slate-50')} required disabled={!isEditMode} />
                      </div>
                      <p className="text-xs text-slate-400">Primer monto del contrato</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Monto Sugerido por Sistema</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">{editContratoForm.moneda === 'USD' ? 'US$' : '$'}</span>
                        <Input
                          type="text"
                          value={montoActHoy && !montoActHoy.error && montoActHoy.monto_actualizado != null ? formatMoneyInput(montoActHoy.monto_actualizado) : '-'}
                          disabled
                          className="pl-10 text-right bg-slate-100 text-slate-700 font-medium"
                          data-testid="monto-sugerido-sistema"
                        />
                      </div>
                      <p className={`text-xs ${montoActHoy?.error ? 'text-red-600' : 'text-slate-400'}`} data-testid="monto-sugerido-explicacion" title={montoActHoy?.explicacion || ''}>
                        {montoActHoy?.error
                          ? `⚠ ${montoActHoy.error}`
                          : montoActHoy?.explicacion
                            ? `Calculado por sistema · corte ${formatDate(montoActHoy.fecha_corte)}`
                            : 'Calculado por sistema'}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs">Monto Confirmado Manualmente</Label>
                        {isEditMode && montoActHoy && !montoActHoy.error && montoActHoy.monto_actualizado != null && (
                          <button
                            type="button"
                            className="text-[11px] text-violet-700 hover:underline"
                            onClick={() => setEditContratoForm({...editContratoForm, monto_actual: montoActHoy.monto_actualizado, fecha_carga_monto: new Date().toISOString().slice(0,10)})}
                            data-testid="usar-sugerido"
                          >Usar sugerido</button>
                        )}
                      </div>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">{editContratoForm.moneda === 'USD' ? 'US$' : '$'}</span>
                        <Input type="text" value={formatMoneyInput(editContratoForm.monto_actual)} onChange={(e) => setEditContratoForm({...editContratoForm, monto_actual: parseMoneyInput(e.target.value), fecha_carga_monto: new Date().toISOString().slice(0,10)})} placeholder="Manual" className={cn("pl-10 text-right", !isEditMode && 'bg-slate-50')} disabled={!isEditMode} data-testid="monto-confirmado-manual" />
                      </div>
                      <p className="text-xs text-slate-400">Valor final revisado · pasa a facturación</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Moneda *</Label>
                      <Select value={editContratoForm.moneda} onValueChange={(v) => setEditContratoForm({...editContratoForm, moneda: v})} disabled={!isEditMode}>
                        <SelectTrigger className={!isEditMode ? 'bg-slate-50' : ''}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ARS">ARS</SelectItem>
                          <SelectItem value="USD">USD</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Ajustes — Multi-índice + Valor referencia USD (Jun 2026) */}
                  <div className="bg-slate-50 rounded-lg p-3 space-y-3">
                    <label className={cn("flex items-center gap-2", isEditMode ? "cursor-pointer" : "opacity-70")}>
                      <input
                        type="checkbox"
                        className="rounded border-slate-300"
                        checked={editContratoForm.indice_tipo === 'sin_ajuste'}
                        disabled={!isEditMode}
                        onChange={(e) => setEditContratoForm({
                          ...editContratoForm,
                          indice_tipo: e.target.checked ? 'sin_ajuste' : 'ipc',
                          ajuste_indices: e.target.checked ? [{ tipo: 'sin_ajuste', peso: 100 }] : null,
                        })}
                        data-testid="sin-ajuste-edit-check"
                      />
                      <span className="text-xs font-medium text-slate-700">Sin ajuste (contrato no se actualiza por índice — ej. USD)</span>
                    </label>
                    {editContratoForm.indice_tipo === 'sin_ajuste' ? (
                      <p className="text-xs text-slate-500">Este contrato no aplica IPC/CAC/Otro. El monto operativo será el Monto Confirmado Manualmente o el monto base vigente.</p>
                    ) : (
                    <>
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-medium text-slate-600">Ajustes — Índices</p>
                      {isEditMode && (
                        <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => {
                          const arr = [...(editContratoForm.ajuste_indices || [])];
                          arr.push({ tipo: 'IPC', peso: 0 });
                          setEditContratoForm({ ...editContratoForm, ajuste_indices: arr });
                        }} data-testid="add-indice-btn">+ Agregar índice</Button>
                      )}
                    </div>
                    {(() => {
                      const indices = editContratoForm.ajuste_indices && editContratoForm.ajuste_indices.length > 0
                        ? editContratoForm.ajuste_indices
                        : [{ tipo: (editContratoForm.indice_tipo || 'ipc').toUpperCase().replace('PORCENTAJE','Otro').replace('OTRO','Otro'), peso: 100 }];
                      const sumaPesos = indices.reduce((s, i) => s + (parseFloat(i.peso) || 0), 0);
                      const ok = Math.abs(sumaPesos - 100) < 0.01;
                      return (
                        <>
                          {indices.map((idx, i) => (
                            <div key={i} className="flex items-center gap-2" data-testid={`indice-row-${i}`}>
                              <Select
                                value={(idx.tipo || 'IPC').toUpperCase()}
                                onValueChange={(v) => {
                                  const arr = [...indices];
                                  arr[i] = { ...arr[i], tipo: v };
                                  setEditContratoForm({ ...editContratoForm, ajuste_indices: arr });
                                }}
                                disabled={!isEditMode}
                              >
                                <SelectTrigger className="h-9 flex-1" data-testid={`indice-tipo-${i}`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="IPC">IPC (INDEC)</SelectItem>
                                  <SelectItem value="CAC">CAC</SelectItem>
                                  <SelectItem value="ICL">ICL</SelectItem>
                                  <SelectItem value="OTRO">Otro</SelectItem>
                                </SelectContent>
                              </Select>
                              <div className="flex items-center gap-1">
                                <Input
                                  type="number" min="0" max="100" step="0.01"
                                  value={idx.peso ?? ''}
                                  onChange={(e) => {
                                    const arr = [...indices];
                                    arr[i] = { ...arr[i], peso: parseFloat(e.target.value) || 0 };
                                    setEditContratoForm({ ...editContratoForm, ajuste_indices: arr });
                                  }}
                                  className="h-9 w-20 text-right"
                                  disabled={!isEditMode}
                                  data-testid={`indice-peso-${i}`}
                                  placeholder="0"
                                />
                                <span className="text-xs text-slate-500">%</span>
                              </div>
                              {isEditMode && indices.length > 1 && (
                                <Button type="button" size="sm" variant="ghost" className="h-9 text-rose-600 px-2"
                                  onClick={() => {
                                    const arr = indices.filter((_, j) => j !== i);
                                    setEditContratoForm({ ...editContratoForm, ajuste_indices: arr });
                                  }}
                                  data-testid={`indice-remove-${i}`}
                                >✕</Button>
                              )}
                            </div>
                          ))}
                          <div className={`text-xs ${ok ? 'text-emerald-600' : 'text-rose-600'} font-medium`} data-testid="suma-pesos">
                            Suma: {sumaPesos.toFixed(2)}% {ok ? '✓' : '· debe ser 100%'}
                          </div>
                        </>
                      );
                    })()}
                    <div className="grid grid-cols-2 gap-4 pt-2 border-t">
                      <div className="space-y-2">
                        <Label className="text-xs">Frecuencia de ajuste</Label>
                        <Select
                          value={editContratoForm.ajuste_frecuencia || editContratoForm.indice_frecuencia || 'trimestral'}
                          onValueChange={(v) => setEditContratoForm({ ...editContratoForm, ajuste_frecuencia: v, indice_frecuencia: v })}
                          disabled={!isEditMode}
                        >
                          <SelectTrigger className="h-9" data-testid="ajuste-frecuencia"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="mensual">Mensual</SelectItem>
                            <SelectItem value="bimestral">Bimestral</SelectItem>
                            <SelectItem value="trimestral">Trimestral</SelectItem>
                            <SelectItem value="cuatrimestral">Cuatrimestral</SelectItem>
                            <SelectItem value="semestral">Semestral</SelectItem>
                            <SelectItem value="anual">Anual</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Valor de referencia (USD)</Label>
                        <Input
                          type="number" min="0" step="0.01"
                          value={editContratoForm.valor_referencia?.monto ?? ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            setEditContratoForm({
                              ...editContratoForm,
                              valor_referencia: v === '' ? null : { monto: parseFloat(v) || 0, moneda: 'USD' },
                            });
                          }}
                          disabled={!isEditMode}
                          className="h-9"
                          placeholder="Ej: 4000"
                          data-testid="valor-referencia-usd"
                        />
                        <p className="text-xs text-slate-400">Piso en USD · se multiplica por cotización BNA para obtener el piso ARS.</p>
                      </div>
                    </div>
                    </>
                    )}
                  </div>
                </div>

                {/* Condición de Cobro */}
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-slate-700 mb-3">Condición de Cobro</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Día de Emisión</Label>
                      <Input type="number" min="1" max="28" value={editContratoForm.dia_emision} onChange={(e) => setEditContratoForm({...editContratoForm, dia_emision: e.target.value})} placeholder="Ej: 1" disabled={!isEditMode} className={!isEditMode ? 'bg-slate-50' : ''} />
                      <p className="text-xs text-slate-400">Día del mes (1-28)</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Día de Vencimiento</Label>
                      <Input type="number" min="1" max="28" value={editContratoForm.dia_vencimiento} onChange={(e) => setEditContratoForm({...editContratoForm, dia_vencimiento: e.target.value})} placeholder="Ej: 10" disabled={!isEditMode} className={!isEditMode ? 'bg-slate-50' : ''} />
                      <p className="text-xs text-slate-400">Día del mes (1-28)</p>
                    </div>
                  </div>
                </div>

                {/* Penalidad */}
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-slate-700 mb-3">Penalidad</h4>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="penalidad_enabled_edit" checked={editContratoForm.penalidad_enabled} onChange={(e) => setEditContratoForm({...editContratoForm, penalidad_enabled: e.target.checked})} className="rounded border-slate-300" disabled={!isEditMode} />
                    <Label htmlFor="penalidad_enabled_edit" className={cn("cursor-pointer text-sm", !isEditMode && "text-slate-500")}>1% diaria sobre el monto adeudado</Label>
                  </div>
                </div>

                {/* Cocheras: valoración por cochera (al final; se oculta si no hay cocheras) */}
                <CocherasModoSection
                  unidadIds={(editContratoForm.unidades_contrato || []).map(uc => uc.unidad_id)}
                  unidadesInfo={unidades || []}
                  valuacion={editContratoForm.cocheras_valuacion || {}}
                  monedaContrato={editContratoForm.moneda || 'ARS'}
                  onChange={(mapa) => setEditContratoForm(prev => ({ ...prev, cocheras_valuacion: mapa }))}
                  disabled={!isEditMode}
                />
              </TabsContent>


              {/* Tab: Fiscal */}
              <TabsContent value="fiscal" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>CUIT</Label>
                    <Input
                      value={editContratoForm.cuit}
                      onChange={(e) => {
                        let value = e.target.value.replace(/\D/g, '');
                        if (value.length > 11) value = value.slice(0, 11);

                        let formatted = '';
                        if (value.length > 0) formatted = value.slice(0, 2);
                        if (value.length > 2) formatted += '-' + value.slice(2, 10);
                        if (value.length > 10) formatted += '-' + value.slice(10, 11);

                        setEditContratoForm({...editContratoForm, cuit: formatted});
                      }}
                      placeholder="20-12345678-9"
                      maxLength={13}
                      disabled={!isEditMode}
                      className={!isEditMode ? 'bg-slate-50' : ''}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Condición IVA</Label>
                    <Select value={editContratoForm.condicion_iva} onValueChange={(v) => setEditContratoForm({...editContratoForm, condicion_iva: v})} disabled={!isEditMode}>
                      <SelectTrigger className={!isEditMode ? 'bg-slate-50' : ''}><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="responsable_inscripto">Resp. Insc.</SelectItem>
                        <SelectItem value="exento">Exento</SelectItem>
                        <SelectItem value="consumidor_final">Consumidor Final</SelectItem>
                        <SelectItem value="monotributista">Monotributista</SelectItem>
                        <SelectItem value="del_exterior">Del Exterior</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Domicilio Fiscal</Label>
                  <Input value={editContratoForm.domicilio_fiscal} onChange={(e) => setEditContratoForm({...editContratoForm, domicilio_fiscal: e.target.value})} placeholder="Dirección completa" disabled={!isEditMode} className={!isEditMode ? 'bg-slate-50' : ''} />
                </div>
                {/* Orden de Compra (reorg: ANTES de Envío de factura) */}
                <div className="space-y-2 pt-3 border-t border-slate-200" data-testid="edit-orden-compra">
                  <Label>Orden de Compra</Label>
                  <Select
                    value={editContratoForm.orden_compra_tipo || 'sin_oc'}
                    onValueChange={(v) => setEditContratoForm({...editContratoForm, orden_compra_tipo: v === 'sin_oc' ? '' : v, orden_compra: v === 'sin_oc' ? '' : editContratoForm.orden_compra})}
                    disabled={!isEditMode}
                  >
                    <SelectTrigger className={!isEditMode ? 'bg-slate-50' : ''}><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sin_oc">Sin OC</SelectItem>
                      <SelectItem value="anual">OC Anual</SelectItem>
                      <SelectItem value="mensual">OC Mensual</SelectItem>
                    </SelectContent>
                  </Select>
                  {editContratoForm.orden_compra_tipo === 'anual' && (
                    <div className="space-y-1">
                      <Label className="text-xs">Número de OC</Label>
                      <Input
                        value={editContratoForm.orden_compra}
                        onChange={(e) => setEditContratoForm({...editContratoForm, orden_compra: e.target.value})}
                        placeholder="Ej: OC-2026-001"
                        disabled={!isEditMode}
                        className={!isEditMode ? 'bg-slate-50' : ''}
                      />
                    </div>
                  )}
                  {editContratoForm.orden_compra_tipo === 'mensual' && (
                    <p className="text-xs text-slate-500 bg-slate-50 p-2 rounded">La OC mensual se carga en la pestaña 5. Factura al generar cada mes.</p>
                  )}
                  <div className="space-y-1">
                    <Label className="text-xs">Instrucción</Label>
                    <Textarea
                      value={editContratoForm.orden_compra_instruccion || ''}
                      onChange={(e) => setEditContratoForm({...editContratoForm, orden_compra_instruccion: e.target.value})}
                      placeholder="Dónde cargar la OC, cuándo pedirla, si se carga todos los meses, si la envía el inquilino, si hay que adjuntarla a la factura..."
                      rows={2}
                      disabled={!isEditMode}
                      className={!isEditMode ? 'bg-slate-50' : ''}
                      data-testid="edit-orden-compra-instruccion"
                    />
                  </div>
                </div>
                {/* ── Envío de factura (reorg) ── */}
                <div className="pt-3 border-t border-slate-200 space-y-3" data-testid="edit-envio-factura">
                  <Label className="text-sm font-medium">Envío de factura</Label>
                  <div className="space-y-1">
                    <Label className="text-xs">¿Cómo se gestiona la factura?</Label>
                    <Select
                      value={editContratoForm.facturacion_metodo || 'email'}
                      onValueChange={(v) => setEditContratoForm({...editContratoForm, facturacion_metodo: v})}
                      disabled={!isEditMode}
                    >
                      <SelectTrigger className={!isEditMode ? 'bg-slate-50' : ''} data-testid="edit-facturacion-metodo"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="portal">Se carga en portal / plataforma</SelectItem>
                        <SelectItem value="email">Se manda por email</SelectItem>
                        <SelectItem value="ambos">Ambas</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-400">Solo registra la forma de gestión. El sistema no envía emails automáticamente.</p>
                  </div>

                  {(() => {
                    const metodo = editContratoForm.facturacion_metodo || 'email';
                    const showEmail = metodo === 'email' || metodo === 'ambos';
                    const showPortal = metodo === 'portal' || metodo === 'ambos';
                    const mismo = editContratoForm.facturacion_mismo_admin !== false;
                    const disabled = !isEditMode || mismo;
                    const cls = disabled ? 'bg-slate-50' : '';
                    const valNombre = mismo ? (editContratoForm.contacto_admin_nombre || '') : (editContratoForm.facturacion_contacto_nombre || '');
                    const valApellido = mismo ? '' : (editContratoForm.facturacion_contacto_apellido || '');
                    const valTel = mismo ? (editContratoForm.contacto_admin_telefono || '') : (editContratoForm.facturacion_contacto_telefono || '');
                    return (
                      <>
                        <div className="flex items-center justify-between">
                          <Label className="text-xs font-medium text-slate-600">Contacto de facturación</Label>
                          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={mismo}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setEditContratoForm(f => ({
                                  ...f,
                                  facturacion_mismo_admin: checked,
                                  ...(checked ? {
                                    facturacion_contacto_nombre: f.contacto_admin_nombre || '',
                                    facturacion_contacto_apellido: '',
                                    facturacion_contacto_telefono: f.contacto_admin_telefono || '',
                                    facturacion_contacto_email: f.contacto_admin_email || '',
                                    emails_facturacion: (() => {
                                      const adminEmail = f.contacto_admin_email || '';
                                      const existing = (f.emails_facturacion && f.emails_facturacion.length) ? f.emails_facturacion : (f.email_facturacion ? [f.email_facturacion] : []);
                                      return adminEmail ? [adminEmail, ...existing.slice(1)] : existing;
                                    })(),
                                  } : {}),
                                }));
                              }}
                              disabled={!isEditMode}
                              data-testid="edit-facturacion-mismo-admin"
                            />
                            Mismo que el contacto administrativo
                          </label>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Nombre</Label>
                            <Input value={valNombre} onChange={(e) => setEditContratoForm({...editContratoForm, facturacion_contacto_nombre: e.target.value})} placeholder="Nombre" disabled={disabled} className={cls} data-testid="edit-facturacion-nombre" />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Apellido</Label>
                            <Input value={valApellido} onChange={(e) => setEditContratoForm({...editContratoForm, facturacion_contacto_apellido: e.target.value})} placeholder="Apellido" disabled={disabled} className={cls} data-testid="edit-facturacion-apellido" />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Teléfono</Label>
                            <Input type="tel" value={valTel} onChange={(e) => setEditContratoForm({...editContratoForm, facturacion_contacto_telefono: e.target.value})} placeholder="+54 261 123-4567" disabled={disabled} className={cls} data-testid="edit-facturacion-telefono" />
                          </div>
                        </div>
                        {showEmail && (
                          <EmailsFacturacionList
                            emails={(editContratoForm.emails_facturacion && editContratoForm.emails_facturacion.length) ? editContratoForm.emails_facturacion : (editContratoForm.email_facturacion ? [editContratoForm.email_facturacion] : [''])}
                            onChange={(next) => setEditContratoForm(f => ({ ...f, emails_facturacion: next, email_facturacion: (next.find(x => (x || '').trim()) || '').trim() }))}
                            disabled={!isEditMode}
                            testidPrefix="edit-facturacion"
                          />
                        )}
                        {showPortal && (
                          <div className="pt-2 border-t border-slate-100 space-y-2" data-testid="edit-proveedor-plataforma">
                            <Label className="text-xs font-medium text-slate-600">Datos de portal / plataforma</Label>
                            <p className="text-xs text-slate-400 -mt-1">Datos de acceso e instrucciones para cargar facturas en el portal del inquilino. Solo se guardan, no se envían ni automatizan.</p>
                            <div className="grid grid-cols-2 gap-3">
                              <div className="space-y-1">
                                <Label className="text-xs">URL / Portal</Label>
                                <Input value={editContratoForm.proveedor_plataforma_url || ''} onChange={(e) => setEditContratoForm({...editContratoForm, proveedor_plataforma_url: e.target.value})} placeholder="https://..." disabled={!isEditMode} className={!isEditMode ? 'bg-slate-50' : ''} data-testid="edit-proveedor-url" />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Usuario</Label>
                                <Input value={editContratoForm.proveedor_plataforma_usuario || ''} onChange={(e) => setEditContratoForm({...editContratoForm, proveedor_plataforma_usuario: e.target.value})} placeholder="usuario" disabled={!isEditMode} className={!isEditMode ? 'bg-slate-50' : ''} data-testid="edit-proveedor-usuario" />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Contraseña</Label>
                                <div className="relative">
                                  <Input
                                    type={showProvPass ? 'text' : 'password'}
                                    value={editContratoForm.proveedor_plataforma_password || ''}
                                    onChange={(e) => setEditContratoForm({...editContratoForm, proveedor_plataforma_password: e.target.value})}
                                    placeholder="••••••••"
                                    disabled={!isEditMode}
                                    className={cn('pr-9', !isEditMode ? 'bg-slate-50' : '')}
                                    data-testid="edit-proveedor-password"
                                  />
                                  <button type="button" onClick={() => setShowProvPass(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="edit-proveedor-password-toggle" tabIndex={-1}>
                                    {showProvPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                  </button>
                                </div>
                              </div>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Instrucciones</Label>
                              <Textarea value={editContratoForm.proveedor_plataforma_instrucciones || ''} onChange={(e) => setEditContratoForm({...editContratoForm, proveedor_plataforma_instrucciones: e.target.value})} placeholder="Dónde cargar la factura, pasos internos, si requiere OC, documentación a adjuntar, comentarios operativos..." rows={3} disabled={!isEditMode} className={!isEditMode ? 'bg-slate-50' : ''} data-testid="edit-proveedor-instrucciones" />
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* Retenciones (reorg: al final) */}
                <div className="grid grid-cols-2 gap-4 pt-3 border-t border-slate-200">
                  <div className="space-y-2">
                    <Label>Retenciones</Label>
                    <Select
                      value={editContratoForm.retencion_tipo || 'sin_retenciones'}
                      onValueChange={(v) => setEditContratoForm({
                        ...editContratoForm,
                        retencion_tipo: v,
                        retencion_porcentaje: v === 'otro' ? (editContratoForm.retencion_porcentaje || '') : null,
                      })}
                      disabled={!isEditMode}
                    >
                      <SelectTrigger data-testid="edit-select-retencion-tipo" className={!isEditMode ? 'bg-slate-50' : ''}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sin_retenciones">Sin retenciones</SelectItem>
                        <SelectItem value="2">2%</SelectItem>
                        <SelectItem value="4">4%</SelectItem>
                        <SelectItem value="6">6%</SelectItem>
                        <SelectItem value="otro">Otro</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-400">Se aplica sobre el monto neto de la factura mensual.</p>
                  </div>
                  {editContratoForm.retencion_tipo === 'otro' && (
                    <div className="space-y-2">
                      <Label>Porcentaje</Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        value={editContratoForm.retencion_porcentaje ?? ''}
                        onChange={(e) => setEditContratoForm({ ...editContratoForm, retencion_porcentaje: e.target.value === '' ? null : parseFloat(e.target.value) })}
                        placeholder="Ej: 3.5"
                        disabled={!isEditMode}
                        className={!isEditMode ? 'bg-slate-50' : ''}
                        data-testid="edit-input-retencion-porcentaje"
                      />
                    </div>
                  )}
                </div>
              </TabsContent>
              <TabsContent value="veraz" className="space-y-4">
                <VerazSection
                  contratoId={editContratoForm?.contrato_id}
                  userRol={userRol}
                  disabled={!isEditMode}
                  defaultInquilinoNombre={editContratoForm?.razon_social || editContratoForm?.nombre_inquilino || ''}
                />
              </TabsContent>

              {/* Tab: Colppy */}
              <TabsContent value="colppy" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nro. Factura</Label>
                    <Select
                      value={editContratoForm.colppy_nro_factura || '004'}
                      onValueChange={(v) => setEditContratoForm({...editContratoForm, colppy_nro_factura: v})}
                      disabled={!isEditMode}
                    >
                      <SelectTrigger className={!isEditMode ? 'bg-slate-50' : ''}><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="004">004</SelectItem>
                        <SelectItem value="003">003</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {(editContratoForm.colppy_nro_factura || '004') === '004' && (
                    <div className="space-y-2">
                      <Label>&nbsp;</Label>
                      <div className="flex items-center h-10 px-3 border rounded-md bg-green-50 border-green-200">
                        <Check className="w-4 h-4 text-green-600 mr-2" />
                        <span className="text-sm text-green-700 font-medium">Factura Electrónica</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Condición de Venta</Label>
                    <Select
                      value={editContratoForm.colppy_condicion_venta}
                      onValueChange={(v) => setEditContratoForm({...editContratoForm, colppy_condicion_venta: v})}
                      disabled={!isEditMode}
                    >
                      <SelectTrigger className={!isEditMode ? 'bg-slate-50' : ''}><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cuenta_corriente">Cuenta Corriente</SelectItem>
                        <SelectItem value="contado">Contado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo Comprobante</Label>
                    <Select
                      value={editContratoForm.colppy_tipo_comprobante}
                      onValueChange={(v) => setEditContratoForm({...editContratoForm, colppy_tipo_comprobante: v})}
                      disabled={!isEditMode}
                    >
                      <SelectTrigger className={!isEditMode ? 'bg-slate-50' : ''}><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Fa">Fa</SelectItem>
                        <SelectItem value="Nd">Nd</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tipo de Factura</Label>
                    <Select
                      value={editContratoForm.colppy_tipo_factura}
                      onValueChange={(v) => setEditContratoForm({...editContratoForm, colppy_tipo_factura: v})}
                      disabled={!isEditMode}
                    >
                      <SelectTrigger className={!isEditMode ? 'bg-slate-50' : ''}><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="A">A</SelectItem>
                        <SelectItem value="B">B</SelectItem>
                        <SelectItem value="E">E</SelectItem>
                        <SelectItem value="Z">Z</SelectItem>
                        <SelectItem value="I">I</SelectItem>
                        <SelectItem value="M">M</SelectItem>
                        <SelectItem value="X">X</SelectItem>
                        <SelectItem value="T">T</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo de Operación</Label>
                    <Select
                      value={editContratoForm.colppy_tipo_operacion}
                      onValueChange={(v) => setEditContratoForm({...editContratoForm, colppy_tipo_operacion: v})}
                      disabled={!isEditMode}
                    >
                      <SelectTrigger className={!isEditMode ? 'bg-slate-50' : ''}><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="servicios">Venta de cosas Muebles, Obras, Locaciones y/o Prestaciones de Servicios</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Descripción</Label>
                  <Input
                    value={editContratoForm.colppy_descripcion}
                    onChange={(e) => setEditContratoForm({...editContratoForm, colppy_descripcion: e.target.value})}
                    placeholder="Servicios de gestión de oficina"
                    disabled={!isEditMode}
                    className={!isEditMode ? 'bg-slate-50' : ''}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Cuenta</Label>
                    <Select
                      value={editContratoForm.colppy_cuenta}
                      onValueChange={(v) => setEditContratoForm({...editContratoForm, colppy_cuenta: v})}
                      disabled={!isEditMode}
                    >
                      <SelectTrigger className={!isEditMode ? 'bg-slate-50' : ''}><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ingresos_oficinas">Ingresos por alquiler Oficinas</SelectItem>
                        <SelectItem value="ingresos_coworking">Ingresos por Coworking</SelectItem>
                        <SelectItem value="ingresos_cocheras">Ingresos por Cocheras</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Moneda</Label>
                    <Select
                      value={editContratoForm.colppy_moneda}
                      onValueChange={(v) => setEditContratoForm({...editContratoForm, colppy_moneda: v})}
                      disabled={!isEditMode}
                    >
                      <SelectTrigger className={!isEditMode ? 'bg-slate-50' : ''}><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ARS">Pesos Argentinos</SelectItem>
                        <SelectItem value="USD">Dólar Estadounidense</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="percepciones_edit"
                      checked={editContratoForm.colppy_percepciones}
                      onChange={(e) => setEditContratoForm({...editContratoForm, colppy_percepciones: e.target.checked})}
                      className="rounded border-slate-300"
                      disabled={!isEditMode}
                    />
                    <Label htmlFor="percepciones_edit" className={cn("cursor-pointer", !isEditMode && "text-slate-500")}>Lleva Percepciones</Label>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="inquilino" className="space-y-4">
                <AccesoInquilinoSection contrato={editContratoForm} unidades={unidades || []} />
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-6">
              <div className="flex items-center justify-between w-full">
                <Button
                  type="button"
                  variant="destructive"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onFinalizarContrato();
                  }}
                >
                  Finalizar Contrato
                </Button>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                    {isEditMode ? 'Cancelar' : 'Cerrar'}
                  </Button>
                  {isEditMode ? (
                    <Button type="submit" disabled={saving}>{saving ? 'Guardando...' : 'Guardar Cambios'}</Button>
                  ) : (
                    <Button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsEditMode(true); }}>Editar</Button>
                  )}
                </div>
              </div>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
